import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnalysisStore } from '../stores/useAnalysisStore';
import { useOrderStore } from '../stores/useOrderStore';
import {
  COURIERS,
  COURIER_IDS,
  STATION_SAFETY_LINE,
} from '@shipping/core';
import type { RiskLevel, CourierId, StationAlert, ParsedOrder } from '@shipping/core';
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Cell,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

const RISK_FILTERS: Array<{ value: RiskLevel | 'all'; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'critical', label: '极高危' },
  { value: 'high', label: '高危' },
  { value: 'medium', label: '中危' },
];

const RISK_COLORS: Record<RiskLevel, string> = {
  critical: 'border-l-red-500 bg-red-50',
  high: 'border-l-orange-500 bg-orange-50',
  medium: 'border-l-yellow-500 bg-yellow-50',
  low: 'border-l-blue-500 bg-blue-50',
  safe: 'border-l-green-500 bg-green-50',
};

const RISK_LABELS: Record<RiskLevel, string> = {
  critical: '极高危',
  high: '高危',
  medium: '中危',
  low: '低危',
  safe: '安全',
};

const RISK_BADGE_COLORS: Record<RiskLevel, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
  safe: 'bg-green-100 text-green-700',
};

/** Severity order for sorting: lower index = more severe. */
const RISK_SEVERITY: RiskLevel[] = ['critical', 'high', 'medium', 'low', 'safe'];

/** Maps riskReason codes to Chinese display labels. */
const RISK_REASON_LABELS: Record<string, string> = {
  perishable: '生鲜食品',
  fragile: '易碎品',
  'high-value': '高价值(>500元)',
  pharmaceutical: '医药用品',
  maternity: '母婴用品',
  live_animal: '宠物活体',
  cold_chain: '冷链食品',
  large_appliance: '大件家电',
  custom: '定制商品',
};

function courierName(id: string): string {
  return COURIERS[id as CourierId]?.name ?? id;
}

interface AddressGroup {
  address: string;
  riskLevel: RiskLevel;
  stationRate: number;
  orderCount: number;
  products: Array<{ category: string; count: number }>;
}

export default function StationAlertPage() {
  const navigate = useNavigate();
  const stationAlerts = useAnalysisStore((s) => s.stationAlerts);
  const parsedOrders = useOrderStore((s) => s.parsedOrders);
  const [riskFilter, setRiskFilter] = useState<RiskLevel | 'all'>('all');

  // Build order lookup map
  const orderMap = useMemo(() => {
    const map = new Map<string, ParsedOrder>();
    for (const o of parsedOrders) map.set(o.id, o);
    return map;
  }, [parsedOrders]);

  // --- Overview cards ---
  const overview = useMemo(() => {
    const totalOrders = parsedOrders.length || 1;
    const highRiskCount = stationAlerts.filter(
      (a) => a.riskLevel === 'critical' || a.riskLevel === 'high',
    ).length;
    const highRiskRatio = highRiskCount / totalOrders;

    const potentialFine = stationAlerts.reduce((sum, a) => sum + a.potentialFine, 0);

    // Affected neighborhoods: unique district values from orders that have alerts
    const affectedDistricts = new Set<string>();
    for (const alert of stationAlerts) {
      const order = orderMap.get(alert.orderId);
      if (order) {
        affectedDistricts.add(order.destination.district);
      }
    }

    // High-risk couriers: most common currentCourier among alerted orders
    const courierCounts: Record<string, number> = {};
    for (const alert of stationAlerts) {
      const order = orderMap.get(alert.orderId);
      if (order?.currentCourier) {
        courierCounts[order.currentCourier] = (courierCounts[order.currentCourier] || 0) + 1;
      }
    }
    const sortedCouriers = Object.entries(courierCounts)
      .sort((a, b) => b[1] - a[1]);
    const totalAlertedWithCourier = sortedCouriers.reduce((sum, [, c]) => sum + c, 0) || 1;
    const topCouriers = sortedCouriers.slice(0, 2).map(([id, count]) => ({
      name: courierName(id),
      percent: ((count / totalAlertedWithCourier) * 100).toFixed(0),
    }));

    return {
      highRiskCount,
      highRiskRatio,
      potentialFine,
      affectedNeighborhoods: affectedDistricts.size,
      topCouriers,
    };
  }, [stationAlerts, parsedOrders, orderMap]);

  // --- Address risk cards (grouped by neighborhood) ---
  const addressGroups = useMemo(() => {
    // Group alerts by destination.district + city
    const groupMap = new Map<string, StationAlert[]>();
    for (const alert of stationAlerts) {
      const order = orderMap.get(alert.orderId);
      if (!order) continue;
      const key = order.destination.district + ', ' + order.destination.city;
      if (!groupMap.has(key)) {
        groupMap.set(key, []);
      }
      groupMap.get(key)!.push(alert);
    }

    const groups: AddressGroup[] = [];
    for (const [address, alerts] of groupMap) {
      // Highest risk level in group
      let highestRisk: RiskLevel = 'safe';
      for (const alert of alerts) {
        if (RISK_SEVERITY.indexOf(alert.riskLevel) < RISK_SEVERITY.indexOf(highestRisk)) {
          highestRisk = alert.riskLevel;
        }
      }

      // Average station rate
      const avgStationRate =
        alerts.reduce((sum, a) => sum + a.stationRate, 0) / alerts.length;

      // Aggregate risky products by category
      const productCounts: Record<string, number> = {};
      for (const alert of alerts) {
        for (const p of alert.riskyProducts) {
          productCounts[p.category] = (productCounts[p.category] || 0) + p.orderCount;
        }
      }
      const products = Object.entries(productCounts)
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count);

      groups.push({
        address,
        riskLevel: highestRisk,
        stationRate: Math.round(avgStationRate),
        orderCount: alerts.length,
        products,
      });
    }

    // Filter by riskFilter
    const filtered =
      riskFilter === 'all'
        ? groups
        : groups.filter((g) => g.riskLevel === riskFilter);

    // Sort by riskLevel severity descending, then orderCount descending
    filtered.sort((a, b) => {
      const severityDiff =
        RISK_SEVERITY.indexOf(a.riskLevel) - RISK_SEVERITY.indexOf(b.riskLevel);
      if (severityDiff !== 0) return severityDiff;
      return b.orderCount - a.orderCount;
    });

    return filtered;
  }, [stationAlerts, orderMap, riskFilter]);

  // --- Courier station rate bar chart data ---
  const courierChartData = useMemo(() => {
    // For each courier, gather station rates from alerts where that courier is the currentCourier
    const courierRates: Record<string, number[]> = {};
    for (const id of COURIER_IDS) {
      courierRates[id] = [];
    }
    for (const alert of stationAlerts) {
      const order = orderMap.get(alert.orderId);
      if (order?.currentCourier && courierRates[order.currentCourier]) {
        courierRates[order.currentCourier].push(alert.stationRate);
      }
    }

    return COURIER_IDS.map((id) => {
      const rates = courierRates[id];
      const avg = rates.length > 0
        ? rates.reduce((s, r) => s + r, 0) / rates.length
        : 0;
      return {
        name: COURIERS[id].name,
        courierId: id,
        stationRate: Math.round(avg * 10) / 10,
        color: COURIERS[id].color,
      };
    });
  }, [stationAlerts, orderMap]);

  // --- Product intercept list ---
  const productIntercepts = useMemo(() => {
    const reasonCounts: Record<string, number> = {};
    for (const alert of stationAlerts) {
      for (const p of alert.riskyProducts) {
        const label = RISK_REASON_LABELS[p.riskReason] ?? p.riskReason;
        reasonCounts[label] = (reasonCounts[label] || 0) + p.orderCount;
      }
    }
    return Object.entries(reasonCounts)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [stationAlerts]);

  // --- Empty state ---
  if (stationAlerts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="text-6xl">🛡️</div>
        <h2 className="text-xl font-semibold text-gray-700">暂无驿站预警数据</h2>
        <p className="text-gray-400">请先上传订单数据并完成分析</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 bg-brand text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          去上传数据
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">末端驿站预警</h1>
        <p className="text-sm text-gray-500 mt-1">
          识别高风险驿站投递地址，规避平台罚款
        </p>
      </div>

      {/* Risk overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500">高危订单</div>
          <div className="text-3xl font-bold text-danger mt-2">
            {overview.highRiskCount} 单
          </div>
          <div className="text-xs text-gray-400 mt-1">
            占比 {(overview.highRiskRatio * 100).toFixed(1)}%
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500">潜在罚款风险</div>
          <div className="text-3xl font-bold text-danger mt-2">
            ¥{overview.potentialFine.toLocaleString()}
          </div>
          <div className="text-xs text-gray-400 mt-1">天猫/抖音单罚 ¥50</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500">涉及小区</div>
          <div className="text-3xl font-bold text-warning mt-2">
            {overview.affectedNeighborhoods} 个
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="text-sm text-gray-500">高危快递</div>
          <div className="text-lg font-semibold text-gray-900 mt-2">
            {overview.topCouriers.length > 0
              ? overview.topCouriers.map((c) => `${c.name}(${c.percent}%)`).join(', ')
              : '暂无数据'}
          </div>
        </div>
      </div>

      {/* Risk filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">风险等级:</span>
        {RISK_FILTERS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setRiskFilter(opt.value)}
            className={[
              'px-3 py-1.5 text-sm rounded-md transition-colors',
              riskFilter === opt.value
                ? 'bg-brand text-white'
                : 'text-gray-600 hover:bg-gray-100',
            ].join(' ')}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Address risk cards */}
      <div className="space-y-4">
        {addressGroups.length === 0 && (
          <div className="text-center text-gray-400 py-8">
            没有符合筛选条件的地址
          </div>
        )}
        {addressGroups.map((card) => (
          <div
            key={card.address}
            className={`border-l-4 rounded-xl p-5 ${RISK_COLORS[card.riskLevel]}`}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${RISK_BADGE_COLORS[card.riskLevel]}`}
                  >
                    {RISK_LABELS[card.riskLevel]}
                  </span>
                  <span className="font-medium text-gray-900">{card.address}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm text-gray-600">
                  <div>
                    驿站投递率:{' '}
                    <span className="font-medium text-danger">{card.stationRate}%</span>
                  </div>
                  <div>
                    涉及订单: <span className="font-medium">{card.orderCount} 单</span>
                  </div>
                </div>
                {card.products.length > 0 && (
                  <div className="text-sm text-gray-500">
                    商品风险:{' '}
                    {card.products.map((p) => `${p.category}${p.count}单`).join(', ')}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button className="px-3 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-white transition-colors">
                  查看订单
                </button>
                <button className="px-3 py-1.5 text-xs bg-brand text-white rounded-md hover:bg-blue-600 transition-colors">
                  一键切换
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Station dependency chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-base font-medium text-gray-900 mb-4">
          各快递驿站投递率对比
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ReBarChart
              data={courierChartData}
              layout="vertical"
              margin={{ top: 5, right: 30, bottom: 5, left: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                type="number"
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                tickFormatter={(v: number) => `${v}%`}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12 }}
                width={50}
              />
              <ReTooltip
                formatter={(value: number) => [`${value}%`, '驿站投递率']}
              />
              <ReferenceLine
                x={STATION_SAFETY_LINE}
                stroke="#52C41A"
                strokeDasharray="4 4"
                label={{
                  value: `安全线 ${STATION_SAFETY_LINE}%`,
                  position: 'top',
                  fill: '#52C41A',
                  fontSize: 12,
                }}
              />
              <Bar dataKey="stationRate" radius={[0, 4, 4, 0]} maxBarSize={28}>
                {courierChartData.map((d) => (
                  <Cell key={d.courierId} fill={d.color} />
                ))}
              </Bar>
            </ReBarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Product intercept list */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-base font-medium text-gray-900 mb-2">商品拦截清单</h3>
        <p className="text-sm text-gray-500 mb-4">
          以下订单商品类目不适合驿站投递，务必使用送货上门快递
        </p>
        <div className="flex flex-wrap gap-6">
          {productIntercepts.length === 0 && (
            <div className="text-sm text-gray-400">暂无高风险商品</div>
          )}
          {productIntercepts.map(([label, count]) => (
            <div key={label} className="text-sm">
              <span className="text-gray-500">{label}:</span>{' '}
              <span className="font-semibold">{count} 单</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
